import React, { useCallback, useEffect, useState } from 'react';

import {
  ComentariosDeCompra as Dados,
  TipoDeReferenciaDeCompra,
  comentarEmCompra,
  editarComentarioDeCompra,
  obterComentariosDeCompra,
  responderComentarioDeCompra,
} from '../../api/comentariosCompraApi';
import { notify } from '../../shared/feedback/notify';
import CampoComMencoes, { MencaoEscolhida, idsMencionadosNoTexto } from './CampoComMencoes';
import TextoEditavel from './TextoEditavel';
import './ComentariosDoVeiculo.css';

const formatarDataHora = (valor: string): string | null => {
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface Props {
  tipo: TipoDeReferenciaDeCompra;
  id: string;
}

/**
 * Seção de comentários de um PEDIDO ou de uma REQUISIÇÃO (at-129), no detalhe de cada um.
 *
 * Irmã de `ComentariosDoVeiculo`, com o mesmo CSS e as mesmas regras (ADR 0042):
 * 1. 🔴 **Falha de carga NUNCA vira "ninguém comentou"** (at-60): para o comprador, isso é
 *    afirmação sobre a compra.
 * 2. **O campo de escrita depende das permissões EFETIVAS** da resposta — a matriz é
 *    configurável, e repetir a regra aqui faria as duas divergirem.
 * 3. **Sem exclusão**; só o autor corrige o texto, nos primeiros 10 minutos e sem resposta
 *    (at-140, ADR 0068).
 * 4. 🔴 **A conversa da REQUISIÇÃO de origem aparece num pedido MARCADA** — sem a marca, o
 *    comprador a leria como conversa deste pedido.
 *
 * ⚠️ Componente próprio, e não uma variação do do veículo: aquele carrega escopo, esteira,
 * sub-tarefa e atendimento anterior, nada disso existe aqui. O que precisa ser UM só — o campo com
 * menção e o texto destacado — é compartilhado.
 */
const ComentariosDeCompra: React.FC<Props> = ({ tipo, id }) => {
  const [dados, setDados] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [texto, setTexto] = useState('');
  const [mencoes, setMencoes] = useState<MencaoEscolhida[]>([]);
  const [respondendoId, setRespondendoId] = useState<string | null>(null);
  const [textoResposta, setTextoResposta] = useState('');
  const [mencoesResposta, setMencoesResposta] = useState<MencaoEscolhida[]>([]);

  const oQue = tipo === 'pedido' ? 'deste pedido' : 'desta requisição';

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await obterComentariosDeCompra(tipo, id));
      setFalhou(false);
    } catch (error) {
      setDados(null);
      setFalhou(true);
      notify.errorFrom(
        error,
        `Não foi possível carregar os comentários ${oQue}. Recarregue a página; se persistir, acione a TI.`,
      );
    } finally {
      setCarregando(false);
    }
  }, [tipo, id, oQue]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const enviarComentario = async () => {
    const limpo = texto.trim();
    if (!limpo || salvando) return;
    setSalvando(true);
    try {
      await comentarEmCompra(tipo, id, limpo, idsMencionadosNoTexto(limpo, mencoes));
      // Só limpa DEPOIS de o backend confirmar: falha com o campo limpo perderia o texto.
      setTexto('');
      setMencoes([]);
      notify.success('Comentário registrado.');
      await carregar();
    } catch (error) {
      // Nunca falso sucesso: quem escreveu precisa saber que não gravou.
      notify.errorFrom(error, 'Não foi possível registrar o comentário. Tente novamente; se persistir, acione a TI.');
    } finally {
      setSalvando(false);
    }
  };

  const enviarResposta = async (comentarioId: string) => {
    const limpo = textoResposta.trim();
    if (!limpo || salvando) return;
    setSalvando(true);
    try {
      await responderComentarioDeCompra(comentarioId, limpo, idsMencionadosNoTexto(limpo, mencoesResposta));
      setTextoResposta('');
      setMencoesResposta([]);
      setRespondendoId(null);
      notify.success('Resposta registrada.');
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível registrar a resposta. Tente novamente; se persistir, acione a TI.');
    } finally {
      setSalvando(false);
    }
  };

  /** at-140: a correção do autor. O erro sobe para o `TextoEditavel`, que mantém o texto no campo. */
  const corrigir = (comentarioId: string) => async (novoTexto: string, mencionados: string[]) => {
    await editarComentarioDeCompra(comentarioId, novoTexto, mencionados);
    await carregar();
  };

  if (carregando && !dados) {
    return (
      <div className="comentarios-veiculo">
        <span className="comentarios-veiculo-carregando">Carregando comentários...</span>
      </div>
    );
  }

  if (falhou) {
    return (
      <div className="comentarios-veiculo">
        <p className="comentarios-veiculo-erro" role="alert">
          Não foi possível verificar os comentários {oQue}. Pode haver comentários que não estão
          sendo exibidos — recarregue a página antes de concluir que ninguém comentou.
        </p>
      </div>
    );
  }

  const threads = Array.isArray(dados?.threads) ? dados!.threads : [];
  const podeComentar = Boolean(dados?.podeComentar);
  const podeResponder = Boolean(dados?.podeResponder);

  return (
    <div className="comentarios-veiculo">
      <div className="comentarios-veiculo-cabecalho">
        <span className="comentarios-veiculo-titulo">Comentários</span>
        {threads.length > 0 && <span className="comentarios-veiculo-contador">{threads.length}</span>}
      </div>

      <p className="comentarios-veiculo-hint">
        Anotações da equipe sobre esta compra. Nada pode ser apagado; quem escreveu pode corrigir o
        texto nos primeiros 10 minutos, enquanto ninguém respondeu.
        {tipo === 'pedido' ? ' As conversas da requisição que originou o pedido aparecem junto, marcadas.' : ''}{' '}
        Digite <strong>@</strong> para mencionar alguém: só quem for escolhido na lista é avisado, e a
        menção substitui o aviso comum que essa pessoa receberia.
      </p>

      {threads.length === 0 ? (
        <p className="comentarios-veiculo-vazio">Nenhum comentário registrado {oQue}.</p>
      ) : (
        <ul className="comentarios-veiculo-lista">
          {threads.map((thread) => {
            const quando = formatarDataHora(thread.criadoEm);
            const daRequisicao = tipo === 'pedido' && thread.origem === 'REQUISICAO';
            return (
              <li key={thread.id} className="comentarios-veiculo-thread">
                <div className="comentarios-veiculo-thread-topo">
                  {daRequisicao && (
                    <span
                      className="comentarios-compra-origem"
                      title="Conversa registrada na requisição que originou este pedido."
                    >
                      Da {thread.origemRotulo.toLowerCase()}
                    </span>
                  )}
                  <span className="comentarios-veiculo-assinatura">
                    {[thread.autorNome, quando].filter(Boolean).join(' - ')}
                  </span>
                </div>

                <TextoEditavel item={thread} temResposta={thread.respostas.length > 0} onSalvar={corrigir(thread.id)} />

                {thread.respostas.length > 0 && (
                  <ul className="comentarios-veiculo-respostas">
                    {thread.respostas.map((resposta) => (
                      <li key={resposta.id} className="comentarios-veiculo-resposta">
                        <span className="comentarios-veiculo-assinatura">
                          {[resposta.autorNome, formatarDataHora(resposta.criadoEm)].filter(Boolean).join(' - ')}
                        </span>
                        <TextoEditavel item={resposta} temResposta={false} onSalvar={corrigir(resposta.id)} />
                      </li>
                    ))}
                  </ul>
                )}

                {podeResponder && respondendoId !== thread.id && (
                  <button
                    type="button"
                    className="comentarios-veiculo-link"
                    onClick={() => {
                      setRespondendoId(thread.id);
                      setTextoResposta('');
                      setMencoesResposta([]);
                    }}
                  >
                    Responder
                  </button>
                )}

                {podeResponder && respondendoId === thread.id && (
                  <div className="comentarios-veiculo-form-resposta">
                    <CampoComMencoes
                      placeholder="Escreva a resposta (use @ para mencionar alguém)"
                      value={textoResposta}
                      onChange={setTextoResposta}
                      mencoes={mencoesResposta}
                      onMencoesChange={setMencoesResposta}
                      ariaLabel="Resposta ao comentário"
                    />
                    <div className="comentarios-veiculo-acoes">
                      <button
                        type="button"
                        className="comentarios-veiculo-btn"
                        disabled={!textoResposta.trim() || salvando}
                        onClick={() => void enviarResposta(thread.id)}
                      >
                        {salvando ? 'Registrando...' : 'Registrar resposta'}
                      </button>
                      <button
                        type="button"
                        className="comentarios-veiculo-btn-secundario"
                        onClick={() => setRespondendoId(null)}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {podeComentar ? (
        <div className="comentarios-veiculo-form">
          <CampoComMencoes
            placeholder="Escreva um comentário. Use @ para mencionar alguém."
            value={texto}
            onChange={setTexto}
            mencoes={mencoes}
            onMencoesChange={setMencoes}
            ariaLabel="Novo comentário"
          />
          <div className="comentarios-veiculo-acoes">
            <button
              type="button"
              className="comentarios-veiculo-btn"
              disabled={!texto.trim() || salvando}
              onClick={() => void enviarComentario()}
            >
              {salvando ? 'Registrando...' : 'Registrar comentário'}
            </button>
          </div>
        </div>
      ) : (
        <p className="comentarios-veiculo-hint">
          Seu perfil pode ler, mas não comentar aqui. Quem comenta é definido na Administração do
          Sistema, em Compras.
        </p>
      )}
    </div>
  );
};

export default ComentariosDeCompra;
